import React from 'react';
import ForecastChart from './ForecastChart';
import StatCardsModule from './modules/StatCardsModule';
import FleetInventoryModule from './modules/FleetInventoryModule';
import OvercommitWarningModule from './modules/OvercommitWarningModule';
import BuildCapacityModule from './modules/BuildCapacityModule';
import BuildCapacityPartsModule from './modules/BuildCapacityPartsModule';
import FleetAdjustmentsModule from './modules/FleetAdjustmentsModule';
import DroneLookupModule from './modules/DroneLookupModule';
import RecentActivityModule from './modules/RecentActivityModule';
import FleetHealthModule from './modules/FleetHealthModule';
import LowPartsModule from './modules/LowPartsModule';
import FlagSummaryModule from './modules/FlagSummaryModule';
import FlagAgingModule from './modules/FlagAgingModule';
import QuickActionsModule from './modules/QuickActionsModule';
import UpcomingEventsModule from './modules/UpcomingEventsModule';
import ShippingZoneAlertsModule from './modules/ShippingZoneAlertsModule';
import SalesPipelineModule from './modules/SalesPipelineModule';
import EventCalendarHeatmapModule from './modules/EventCalendarHeatmapModule';
import TeamActivityModule from './modules/TeamActivityModule';
import UserActivitySummaryModule from './modules/UserActivitySummaryModule';
import WorkOrderQueueModule from './modules/WorkOrderQueueModule';
import AssemblyProgressModule from './modules/AssemblyProgressModule';
import MyShowsModule from './modules/MyShowsModule';
import AirspaceAuthModule from './modules/AirspaceAuthModule';
import StaleBoxesModule from './modules/StaleBoxesModule';
import ChargeStatusModule from './modules/ChargeStatusModule';
import { instanceAllows } from '../navigation/menuConfig';

/**
 * Check if a user has the required capability to see a module.
 *
 * @param {string} moduleId - The module registry key
 * @param {Object} user - User object from useUserStore
 * @returns {boolean}
 */
export const userCanSeeModule = (moduleId, user) => {
  const mod = MODULE_REGISTRY[moduleId];
  if (!mod) return false;

  // Instance gate, orthogonal to the capability gate below — both must pass.
  // Absent `instances` means Firefleet-only, matching navTree.ts. Allowlist,
  // never denylist. See docs/operations/two-instances.md.
  if (!instanceAllows(mod)) return false;

  const cap = mod.requiredCapability;
  if (!cap) return true;

  if (cap === 'hasRole') {
    return !!(user?.isadmin || user?.can_admin || user?.can_operations ||
              user?.can_manufacturing || user?.can_producer || user?.can_sales ||
              user?.can_pilot || user?.can_procurement);
  }

  if (Array.isArray(cap)) {
    return cap.some(c => {
      if (c === 'admin') return !!(user?.isadmin || user?.can_admin);
      if (c === 'operations') return !!(user?.can_operations || user?.isadmin);
      if (c === 'manufacturing') return !!(user?.can_manufacturing || user?.isadmin);
      if (c === 'sales') return !!(user?.can_sales || user?.isadmin);
      if (c === 'producer') return !!(user?.can_producer || user?.isadmin);
      if (c === 'pilot') return !!(user?.can_pilot || user?.isadmin);
      if (c === 'manager') return !!(user?.can_manager || user?.isadmin);
      return false;
    });
  }

  return false;
};

/**
 * Module registry — the single source of truth for all dashboard modules.
 * To add a new module: add an entry here and write the component. That's it.
 *
 * requiredCapability:
 *   null/undefined  = everyone can see
 *   'hasRole'       = any capability flag
 *   ['operations', 'admin'] = one of these specific capabilities
 *
 * defaultSize: 'full' | 'half'
 *   full = spans full width
 *   half = takes half width on desktop (two per row)
 */
// Visible on both deployments. Absent means Firefleet-only — allowlist, never
// denylist, matching navTree.ts. See docs/operations/two-instances.md.
const BOTH = ['firefly', 'swarm'];

export const MODULE_REGISTRY = {
  recent_activity: {
    id: 'recent_activity', instances: BOTH, label: 'Recent Activity', description: 'Your recently touched drones',
    defaultVisible: true, defaultSize: 'full', requiredCapability: null,
    render: () => <RecentActivityModule />,
  },
  drone_lookup: {
    id: 'drone_lookup', instances: BOTH, label: 'Drone Lookup', description: 'Quick serial lookup with hex keyboard and QR',
    defaultVisible: true, defaultSize: 'full', requiredCapability: null,
    render: () => <DroneLookupModule />,
  },
  forecast_chart: {
    id: 'forecast_chart', label: 'Availability Forecast', description: 'Fleet availability projection over 6 months',
    defaultVisible: false, defaultSize: 'full', requiredCapability: 'hasRole',
    render: () => <ForecastChart />,
  },
  stat_cards: {
    id: 'stat_cards', instances: BOTH, label: 'Fleet Stats', description: 'Available, Flagged, and Batteries at a glance',
    defaultVisible: false, defaultSize: 'full', requiredCapability: 'hasRole',
    render: (props) => <StatCardsModule fleetCounts={props.fleetCounts} available={props.available} />,
  },
  fleet_inventory: {
    id: 'fleet_inventory', instances: BOTH, label: 'Fleet Inventory', description: 'Internal, Sellable fleet and battery counts',
    defaultVisible: false, defaultSize: 'full', requiredCapability: ['operations', 'admin'],
    render: (props) => <FleetInventoryModule fleetCounts={props.fleetCounts} onRefresh={props.onRefresh} />,
  },
  overcommit_warning: {
    id: 'overcommit_warning', label: 'Over-Commitment Alert', description: 'Warning when fleet allocation exceeds total',
    defaultVisible: false, defaultSize: 'full', requiredCapability: ['operations', 'admin'],
    render: (props) => <OvercommitWarningModule fleetCounts={props.fleetCounts} />,
  },
  build_capacity: {
    id: 'build_capacity', instances: BOTH, label: 'Build Capacity', description: 'Builds today per product, from its parts list',
    defaultVisible: false, defaultSize: 'full', requiredCapability: ['manufacturing', 'operations', 'admin'],
    render: (props) => <BuildCapacityModule buildCapacity={props.buildCapacity} />,
  },
  build_capacity_parts: {
    id: 'build_capacity_parts', instances: BOTH, label: 'Capacity By Part', description: 'The parts that limit each product, from its parts list',
    defaultVisible: false, defaultSize: 'full', requiredCapability: ['manufacturing', 'operations', 'admin'],
    render: (props) => <BuildCapacityPartsModule buildCapacity={props.buildCapacity} />,
  },
  fleet_adjustments: {
    id: 'fleet_adjustments', instances: BOTH, label: 'Fleet Adjustments', description: 'Manual fleet change audit log',
    defaultVisible: false, defaultSize: 'full', requiredCapability: ['operations', 'admin'],
    render: (props) => <FleetAdjustmentsModule adjustments={props.adjustments} onRefresh={props.onRefresh} />,
  },

  // ---- Compact modules (half-width by default) ----

  fleet_health: {
    id: 'fleet_health', instances: BOTH, label: 'Fleet Health', description: 'Overall fleet readiness score',
    defaultVisible: false, defaultSize: 'half', requiredCapability: null,
    render: () => <FleetHealthModule />,
  },
  flag_summary: {
    id: 'flag_summary', instances: BOTH, label: 'Flag Summary', description: 'Active flags by category',
    defaultVisible: false, defaultSize: 'half', requiredCapability: 'hasRole',
    render: () => <FlagSummaryModule />,
  },
  upcoming_events: {
    id: 'upcoming_events', label: 'Upcoming Events', description: 'Next 5 events with countdowns',
    defaultVisible: false, defaultSize: 'half', requiredCapability: 'hasRole',
    render: () => <UpcomingEventsModule />,
  },
  shipping_zone_alerts: {
    id: 'shipping_zone_alerts', label: 'Shipping Alerts', description: 'Events in red/orange/yellow shipping zones',
    defaultVisible: false, defaultSize: 'half', requiredCapability: ['operations', 'producer', 'admin'],
    render: () => <ShippingZoneAlertsModule />,
  },
  airspace_auth: {
    id: 'airspace_auth', label: 'Airspace Authorizations', description: 'FAA authorization status across upcoming events',
    defaultVisible: false, defaultSize: 'half', requiredCapability: ['pilot', 'operations', 'admin'],
    render: () => <AirspaceAuthModule />,
  },
  charge_status: {
    id: 'charge_status', label: 'Charge Status', description: 'Battery status for your selected charge location',
    defaultVisible: false, defaultSize: 'half', requiredCapability: ['pilot', 'operations', 'manager', 'manufacturing', 'admin'],
    render: () => <ChargeStatusModule />,
  },
  event_calendar_heatmap: {
    id: 'event_calendar_heatmap', label: 'Event Heat Map', description: '8-week commitment density',
    defaultVisible: false, defaultSize: 'half', requiredCapability: 'hasRole',
    render: () => <EventCalendarHeatmapModule />,
  },
  sales_pipeline: {
    id: 'sales_pipeline', instances: BOTH, label: 'Sales Pipeline', description: 'Orders by status with funnel visualization',
    defaultVisible: false, defaultSize: 'half', requiredCapability: ['sales', 'admin'],
    render: () => <SalesPipelineModule />,
  },
  low_parts: {
    id: 'low_parts', instances: BOTH, label: 'Low Stock Alert', description: 'Parts below minimum stock levels',
    defaultVisible: false, defaultSize: 'half', requiredCapability: ['operations', 'manufacturing', 'admin'],
    render: () => <LowPartsModule />,
  },
  stale_boxes: {
    id: 'stale_boxes', instances: BOTH, label: 'Stale Receipt Boxes', description: 'Receipt boxes whose first-flash is past the family stale-box threshold',
    defaultVisible: false, defaultSize: 'half', requiredCapability: ['manufacturing', 'operations', 'admin'],
    render: () => <StaleBoxesModule />,
  },
  flag_aging: {
    id: 'flag_aging', instances: BOTH, label: 'Flag Aging', description: 'Stale flags open longer than 7 days',
    defaultVisible: false, defaultSize: 'half', requiredCapability: 'hasRole',
    render: () => <FlagAgingModule />,
  },
  team_activity: {
    id: 'team_activity', instances: BOTH, label: 'Team Activity', description: 'Recent actions across the fleet',
    defaultVisible: false, defaultSize: 'half', requiredCapability: 'hasRole',
    render: () => <TeamActivityModule />,
  },
  user_activity_summary: {
    id: 'user_activity_summary', instances: BOTH, label: 'User Activity', description: 'Activity breakdown by team member',
    defaultVisible: false, defaultSize: 'half', requiredCapability: ['admin'],
    render: () => <UserActivitySummaryModule />,
  },
  quick_actions: {
    id: 'quick_actions', instances: BOTH, label: 'Quick Actions', description: 'Shortcut buttons to common pages',
    defaultVisible: false, defaultSize: 'full', requiredCapability: null,
    render: () => <QuickActionsModule />,
  },
  work_order_queue: {
    id: 'work_order_queue', instances: BOTH, label: 'Work Orders', description: 'Manufacturing work order queue (coming soon)',
    defaultVisible: false, defaultSize: 'half', requiredCapability: ['manufacturing', 'admin'],
    render: () => <WorkOrderQueueModule />,
  },
  assembly_progress: {
    id: 'assembly_progress', instances: BOTH, label: 'Assembly Progress', description: 'Daily assembly counts and targets (coming soon)',
    defaultVisible: false, defaultSize: 'half', requiredCapability: ['manufacturing', 'admin'],
    render: () => <AssemblyProgressModule />,
  },
  my_shows: {
    id: 'my_shows', label: 'My Upcoming Shows', description: 'Events you are assigned to as crew',
    defaultVisible: false, defaultSize: 'half', requiredCapability: null,
    render: () => <MyShowsModule />,
  },
};

/**
 * IDs of modules that require the /api/dashboard data.
 * Used by FleetDashboard to conditionally skip the API call for basic users.
 */
export const DASHBOARD_DATA_MODULES = new Set([
  'forecast_chart', 'stat_cards', 'fleet_inventory',
  'overcommit_warning', 'build_capacity', 'build_capacity_parts', 'fleet_adjustments',
]);

/**
 * Default config — used when user has no saved config (NULL in DB).
 */
export const DEFAULT_CONFIG = {
  version: 3,
  modules: Object.values(MODULE_REGISTRY).map((mod, i) => ({
    id: mod.id,
    visible: mod.defaultVisible,
    size: mod.defaultSize || 'full',
    order: i,
  })),
};

/**
 * Merge a user's saved config with the registry.
 * - Preserves user's ordering and visibility for known modules
 * - Appends new modules (added after user last saved) at the end
 * - Removes modules that no longer exist in the registry
 */
export const getOrderedModules = (userConfig) => {
  if (!userConfig || !userConfig.modules) {
    return DEFAULT_CONFIG.modules.map(m => ({ ...m }));
  }

  const registryIds = new Set(Object.keys(MODULE_REGISTRY));

  const knownModules = userConfig.modules
    .filter(m => registryIds.has(m.id))
    .sort((a, b) => (a.order ?? Infinity) - (b.order ?? Infinity));

  const userIds = new Set(knownModules.map(m => m.id));
  // Ensure each known module has a size (backfill for older configs)
  const withSize = knownModules.map(m => ({
    ...m,
    size: m.size || MODULE_REGISTRY[m.id]?.defaultSize || 'full',
  }));

  const newModules = Object.keys(MODULE_REGISTRY)
    .filter(id => !userIds.has(id))
    .map((id, i) => ({
      id,
      visible: MODULE_REGISTRY[id].defaultVisible,
      size: MODULE_REGISTRY[id].defaultSize || 'full',
      order: withSize.length + i,
    }));

  return [...withSize, ...newModules];
};
